/* Move this eventually
  We can use themes to enable Material UI components to use our custom fonts and colors by default
  We can also set defaults for other properties like typography, spacing, etc.

  Usage: Wrap the highest level of your component tree with ThemeProvider and provide theme as prop

  <ThemeProvider theme={theme}>
    <YourApp />
  </ThemeProvider>
*/

import { createTheme } from '@mui/material/styles';

const DEFAULT_FONT_SIZE = '1.125rem';

const theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: ({ theme }) => {
        return {
          body: {
            fontSize: DEFAULT_FONT_SIZE,
            // This RGB value was taken directly from the E-commerce site, not sure
            // it's worth converting although it would admittedly be more consistent
            color: 'rgb(85, 85, 85)',
          },
          p: {
            fontSize: DEFAULT_FONT_SIZE,
          },
          hr: {
            borderColor: '#dfe3e6',
            width: '100%',
          },
          blockquote: {
            borderLeft: 0,
          },
          strong: {
            fontFamily: 'Whitney Bold',
            fontWeight: 'bold',
          },
        };
      },
    },
    // Name of the component
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        // Name of the slot
        root: ({ theme, ownerState }) => {
          const overrides = {
            minHeight: '3.125rem',
            fontSize: '1rem',
            borderRadius: '0',
            lineHeight: '1.25rem',
            textTransform: 'uppercase',
            boxShadow: 'none',
            padding: '0.8125rem 2.25rem', // Our application style sheet makes anchor tag font-colors override their parent Button component's font-color
            // This unsets the color any anchor tag that's a direct child of a MuiButton component.
            '& > a': { color: 'unset', width: '100%' },
          };
          // button variants are 'text', 'outlined', and 'contained'
          if (ownerState.variant === 'text') {
            // Override application CSS button:focus style rule
            overrides['&:focus'] = { backgroundColor: 'transparent' };
            overrides['&:hover'] = {
              color: theme.palette.primary.dark,
              backgroundColor: 'initial',
            };
            overrides.textTransform = 'capitalize';
            if (ownerState.variant.disabled) {
              overrides.color = theme.palette.disabled.main;
            }
          } else if (ownerState.variant === 'contained') {
            if (
              ownerState.color === 'primary' ||
              ownerState.color === 'error' ||
              ownerState.color === 'secondary'
            ) {
              overrides['&:hover'] = {
                backgroundColor: theme.palette[ownerState.color].dark,
                boxShadow: 'none',
              };
              overrides['&:focus'] = {
                backgroundColor: theme.palette[ownerState.color].dark,
                boxShadow: 'none',
              };
            } else {
              overrides['&:hover'] = {
                boxShadow: 'none',
              };
              overrides['&:focus'] = {
                boxShadow: 'none',
              };
            }
          } else if (ownerState.variant === 'outlined') {
            const outlinedButtonBorderWidth = '0.125rem';

            overrides.borderWidth = outlinedButtonBorderWidth;
            overrides.borderColor = theme.palette.primary.main;

            overrides['&:hover'] = {
              color: '#fff',
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.main,
              borderWidth: outlinedButtonBorderWidth,
            };
            overrides['&:focus'] = {
              color: theme.palette.primary.dark,
              borderColor: theme.palette.primary.dark,
              backgroundColor: 'initial',
              borderWidth: outlinedButtonBorderWidth,
            };
          }

          return overrides;
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '2.5rem',
          paddingTop: '0.625rem',
          justifyContent: 'flex-start',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridColumnGap: '1rem',
          '@media screen and (max-width: 600px)': {
            padding: '1.5rem',
          },
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& button:focus': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& .Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: '#fff',
            '&:hover, &:focus': {
              backgroundColor: theme.palette.primary.main,
            },
          },
        }),
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          '& .MuiPickersLayout-toolbar': {
            display: 'none',
          },
          '& .MuiPickersLayout-actionBar': {
            width: '100%',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '2.5rem',
          paddingTop: '0',
          color: '#272727',
          '@media screen and (max-width: 600px)': {
            padding: '1.5rem',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.625rem',
          color: '#272727',
          lineHeight: '1.3',
          padding: '2.5rem',
          paddingBottom: '0.625rem',
          '@media screen and (max-width: 600px)': {
            padding: '1.5rem',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: 'Whitney Medium',
          color: '#000',
          position: 'relative',
          top: '-6px',
          overflow: 'visible',
          whiteSpace: 'unset',
          '& > .disabled': { color: '#888888' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& > .outlined-select': {
            '&:focus': {
              backgroundColor: 'inherit',
              borderColor: '#006c9c',
            },
          },
        },
      },
    },
    MuiStack: {
      styleOverrides: {
        root: {
          '& > hr:not(style)': {
            marginTop: 0,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '0 1rem',
          '@media screen and (max-width: 50rem)': {
            padding: '1rem',
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          p: 'p',
        },
      },
    },
  },
  typography: {
    fontFamily: ['Whitney Book'].join(','),
    h1: {
      lineHeight: '1.6875rem',
      fontFamily: 'Whitney Book',
      fontWeight: 400,
      fontSize: '1.625rem',
      '@media (min-width:768px)': {
        fontSize: '2.625rem',
      },
    },
    h2: {
      fontFamily: 'Whitney Light',
      fontSize: '1.25rem',
      fontWeight: 600,
      '@media (min-width:768px)': {
        fontSize: '1.625rem',
      },
    },
    h3: {
      fontFamily: 'Whitney Medium',
      fontSize: '1.125rem',
      fontWeight: 500,
    },
    h6: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    body1: {
      /*
        Set the default styles for Typography components with variant='body1'
        Ref: https://mui.com/material-ui/react-typography/#changing-the-semantic-element

        Rendering a Typography component with variant='body1' will render a <p> tag by default
        'body1' is the name of the default variant for the Typography component
        The Typography component has a prop named variantMapping that associates a UI "variant" with an underlying semantic element
        The defalt variantMapping is:
        { h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6', subtitle1: 'h6', subtitle2: 'h6', body1: 'p', body2: 'p', inherit: 'p', }
        That's why we get a <p> tag when we render a Typography component with variant='body1'
      */
      marginBottom: '0',
      lineHeight: 'normal',
    },
    p: {
      /*
        body1 is the default variant for Typography components and it renders a <p> tag by default so we could theoretically just
        render p tags via <Typography>My paragraph text</Typography> and it would work since the default variant is 'body1'
        and 'body1' maps to a <p> tag

        It seems less confusing to use a variant name of "p" rather than "body1" when we want to render a <p> tag
        So we can create styles for the "p" variant and add p to the variantMapping

        Then we can render a Typography component with variant='p' and it will render a <p> tag w/o the marginBottom global application stylesheet
          <Typography variant='p'>My paragraph text</Typography>
      */
      marginBottom: '0',
    },
    button: {
      fontFamily: 'Whitney Bold',
    },
  },
  palette: {
    primary: {
      main: '#006c9c',
      dark: '#004766',
    },
    secondary: {
      light: '#eee',
      main: '#888',
      dark: '#6e6e6e',
    },
    neutral: {
      main: '#272727',
      contrastText: '#fff',
    },
    success: {
      main: '#059347',
      light: '#cde9da',
      dark: '#034c25',
    },
    error: {
      main: '#bf1e2d',
      dark: '#85151f',
    },
    disabled: {
      main: '#a6a6a6',
    },
    warning: {
      main: '#ffc646',
    },
  },
});

export default theme;
